import _defineProperty from "/builds/speachme/speachmeDesktop/speach-front/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { __decorate } from "tslib";
import { Component, Prop, Vue } from "vue-facing-decorator";
import axios from "axios";
import WDialog from "@/components/wrappers/w-dialog.vue";
import WInput from "@/components/form/w-input.vue";
import WSelect from "@/components/form/w-select.vue";
import StandardButton from "@/components/buttons/StandardButton.vue";
import Icon from "@/components/icon/Icon.vue";
import Loader from "@/components/loader/Loader.vue";
import WProgress from "@/components/wrappers/w-progress.vue";
import WToolTip from "@/components/tooltip/w-toolTip.vue";
import AiCreditsToolTip from "@/components/tooltip/AiCreditsToolTip.vue";
import delay from "delay";
import { IMAGE_STYLES } from "@/util/imageStyle";
import { alertError, alertErrorDialog } from "@/util/dialog";
export const OPEN_THUMBNAIL_GENERATOR = "sp:open-thumbnail-generator";
export const THUMBNAIL_GENERATED = "sp:thumbnail-generated";
// advanced generation uses a slower model, it gets extra time on both the poll timeout and the fake progress
const ADVANCED_EXTRA_TIME = 30000;
const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 120000;
const MAX_RETRIES = 2;
const PROGRESS_INTERVAL = 100;
const PROGRESS_FAKE_TIME = 8000;
const INSTRUCTIONS_TIMEOUT = 60000;
// sentinel returned by speach-llm when the course is too thin to build a prompt, mapped to an empty textarea
const NO_THUMBNAIL_PROMPT = "NO_PROMPT";
let ThumbnailGeneratorModal = class ThumbnailGeneratorModal extends Vue {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "courId", void 0);
    // when the user has no AI credits left, advanced generation is off and locked
    _defineProperty(this, "aiCreditsAvailable", void 0);
    _defineProperty(this, "isVisible", false);
    _defineProperty(this, "step", "prompt");
    _defineProperty(this, "instructions", "");
    _defineProperty(this, "instructionsLoading", false);
    // once instructions are generated or typed, they are kept in memory so re-opening the modal never overwrites them
    _defineProperty(this, "instructionsLoaded", false);
    _defineProperty(this, "styleSelected", IMAGE_STYLES[0].value);
    _defineProperty(this, "advanced", false);
    _defineProperty(this, "saving", false);
    _defineProperty(this, "previewImageB64", "");
    _defineProperty(this, "jobID", "");
    _defineProperty(this, "generating", false);
    _defineProperty(this, "fakeProgress", 0);
  }
  mounted() {
    window.addEventListener(OPEN_THUMBNAIL_GENERATOR, this.open);
  }
  beforeUnmount() {
    window.removeEventListener(OPEN_THUMBNAIL_GENERATOR, this.open);
  }
  get imageStyles() {
    return IMAGE_STYLES;
  }
  get previewImageUrl() {
    return "data:image/jpeg;base64," + this.previewImageB64;
  }
  get progressValue() {
    return Math.round(this.fakeProgress);
  }
  async open() {
    this.reset();
    this.isVisible = true;
    // generate the instructions only the first time, a re-open keeps what was generated or typed before
    if (!this.instructionsLoaded) {
      await this.loadInstructions();
    }
  }
  onInstructionsInput(value) {
    this.instructions = value;
    // the user provided their own text, do not auto-generate over it on the next open
    this.instructionsLoaded = true;
  }
  close() {
    this.isVisible = false;
    this.generating = false;
  }
  reset() {
    this.step = "prompt";
    // advanced generation is enabled by default, unless the user ran out of AI credits
    this.advanced = this.aiCreditsAvailable;
    this.saving = false;
    this.previewImageB64 = "";
    this.jobID = "";
    this.fakeProgress = 0;
    this.styleSelected = IMAGE_STYLES[0].value;
  }
  async loadInstructions() {
    this.instructionsLoading = true;
    try {
      const resp = await axios.get(`/recorder/settings/generateThumbnailInstructions/${this.courId}`);
      const instructions = await this.pollInstructions(resp.data.jobID);
      this.instructions = instructions === NO_THUMBNAIL_PROMPT ? "" : instructions;
      // a completed generation counts as loaded, even when empty, so we don't re-generate on the next open
      this.instructionsLoaded = true;
    } catch (e) {
      // load failed, keep an empty prompt and allow a retry on the next open
    } finally {
      this.instructionsLoading = false;
    }
  }
  async pollInstructions(jobID) {
    let elapsed = 0;
    while (elapsed < INSTRUCTIONS_TIMEOUT) {
      const resp = await axios.get(`/recorder/jobID/${jobID}`);
      if (resp.data === "ERROR" || resp.data?.errorData) {
        return "";
      }
      if ((resp.data + "").length > 0) {
        return (resp.data + "").toString();
      }
      await delay(POLL_INTERVAL);
      elapsed += POLL_INTERVAL;
    }
    return "";
  }
  async generate() {
    // A prompt is required — never generate an image from an empty prompt.
    if (!this.instructions.trim()) {
      return;
    }
    this.generating = true;
    this.step = "generating";
    this.previewImageB64 = "";
    // Run the fake progress bar ALONGSIDE the generation (not before it): it
    // reassures the user while the image is generated, and stops as soon as the
    // image arrives (the loop exits when `generating` flips to false below).
    this.launchFakeProgress();
    try {
      const image = await this.attemptGenerationWithRetry();
      this.generating = false;
      this.previewImageB64 = image;
      this.step = "preview";
    } catch (e) {
      this.generating = false;
      this.step = "prompt";
      let messageKey = "thumbnailGenerator.errorGeneration";
      if (e?.code === "INSUFFICIENT_CREDITS") {
        messageKey = "thumbnailGenerator.insufficientCredits";
      } else if (e?.isTimeout) {
        messageKey = "thumbnailGenerator.errorTimeout";
      }
      await alertErrorDialog(this.$t(messageKey).toString(), this.$t("thumbnailGenerator.errorTitle").toString(), this.$t("alertDialog.ok").toString());
    }
  }
  async attemptGenerationWithRetry() {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await this.attemptGeneration();
      } catch (e) {
        lastError = e;
        // only a timeout is retried, an explicit error (insufficient credits, failed generation) is not
        if (e && e.isTimeout && attempt < MAX_RETRIES) {
          continue;
        }
        throw e;
      }
    }
    throw lastError;
  }
  async attemptGeneration() {
    // Play only binds form encoded params, a JSON body would not be bound to the action params
    const formData = new FormData();
    formData.append("instructions", this.instructions);
    formData.append("style", this.styleSelected);
    formData.append("advanced", this.advanced + "");
    const resp = await axios.post(`/recorder/settings/generateThumbnailImage/${this.courId}`, formData);
    if (resp.data && resp.data.error) {
      const error = new Error(resp.data.error);
      error.code = resp.data.error;
      throw error;
    }
    this.jobID = resp.data.jobID;
    const timeout = POLL_TIMEOUT + (this.advanced ? ADVANCED_EXTRA_TIME : 0);
    let elapsed = 0;
    let image = "";
    while (!image) {
      await delay(POLL_INTERVAL);
      elapsed += POLL_INTERVAL;
      if (elapsed >= timeout) {
        const timeoutError = new Error("Timeout waiting for thumbnail generation");
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      const pollResp = await axios.get(`/recorder/jobID/${this.jobID}`);
      if (pollResp.data === "ERROR" || pollResp.data?.errorData) {
        throw new Error("Thumbnail generation failed");
      }
      if ((pollResp.data + "").length > 0) {
        image = (pollResp.data + "").toString();
      }
    }
    return image;
  }
  async launchFakeProgress() {
    this.fakeProgress = 0;
    const totalTime = PROGRESS_FAKE_TIME + (this.advanced ? ADVANCED_EXTRA_TIME : 0);
    for (let time = 0; time < totalTime; time += PROGRESS_INTERVAL) {
      await delay(PROGRESS_INTERVAL);
      if (!this.generating) {
        return;
      }
      this.fakeProgress = Math.min(99, time / totalTime * 100);
    }
  }
  backToPrompt() {
    this.step = "prompt";
    this.previewImageB64 = "";
  }
  async validate() {
    this.saving = true;
    try {
      // the image is read back from the job cache, we only send the jobID
      const formData = new FormData();
      formData.append("jobID", this.jobID);
      const resp = await axios.post(`/recorder/settings/saveThumbnailImage/${this.courId}`, formData);
      if (resp.data && resp.data.success) {
        window.dispatchEvent(new CustomEvent(THUMBNAIL_GENERATED, {
          detail: {
            url: resp.data.dataValue
          }
        }));
        this.close();
      } else {
        alertError(this.$t("thumbnailGenerator.error").toString());
      }
    } catch (e) {
      alertError(this.$t("thumbnailGenerator.error").toString());
    } finally {
      this.saving = false;
    }
  }
};
__decorate([Prop({
  required: true
})], ThumbnailGeneratorModal.prototype, "courId", void 0);
__decorate([Prop({
  default: true
})], ThumbnailGeneratorModal.prototype, "aiCreditsAvailable", void 0);
ThumbnailGeneratorModal = __decorate([Component({
  components: {
    WDialog,
    WInput,
    WSelect,
    StandardButton,
    Icon,
    Loader,
    WProgress,
    WToolTip,
    AiCreditsToolTip
  }
})], ThumbnailGeneratorModal);
export default ThumbnailGeneratorModal;